import { Button, Icon, InlineStack, Spinner, Tooltip } from '@customerio/pluma-components/react';
import { useFormContext, useWatch } from '@/components/form';
import type { Integration, IntegrationSetting } from '@/services/db/integration/entity';
import { useVerifyDataOutIntegration } from '@/services/db/data-out-integration/mutations';
import { getUserFacingErrorMessage } from '@/services/api/errors';
import { hasAllSecrets, type DataOutFormValues } from '../form/schema';

type Snackbars = { create: (options: { variant: 'default' | 'critical'; id: string; message: string }) => () => void };

type TestConnectionButtonProps = {
  integration: Integration;
  settings: readonly IntegrationSetting[];
  isFormDisabled: boolean;
  snackbars: Snackbars;
  /** The workspace the page resolved; not read from the URL for the same reason as the writes. */
  workspaceId: number;
};

/**
 * Sends a sample event through the connector's current settings.
 *
 * The result is transient feedback beside the button, not saved state. A failed test comes back as
 * a 400, so success and failure are the mutation's two outcomes rather than a field on the response.
 */
export function TestConnectionButton({
  integration,
  settings,
  isFormDisabled,
  snackbars,
  workspaceId,
}: TestConnectionButtonProps) {
  const { control, trigger, getValues } = useFormContext<DataOutFormValues>();
  const currentSettings = useWatch({ control, name: 'settings' });
  const verify = useVerifyDataOutIntegration({ workspaceId });

  // Every secret must be present even on a connected connector: the API never returns stored
  // secrets, so it cannot authenticate a test from the saved record alone.
  const isDisabled = isFormDisabled || verify.isPending || !hasAllSecrets(settings, currentSettings);

  const onTest = async () => {
    const isFormValid = await trigger('settings');
    if (!isFormValid || isDisabled) {
      return;
    }

    try {
      const result = await verify.mutateAsync({
        identifier: integration.id,
        settings: getValues('settings'),
      });

      if (result.alert != null) {
        snackbars.create({
          variant: 'default',
          id: 'verify.data-out.success',
          message: `${result.alert.title}: ${result.alert.content}`,
        });
      }
    } catch (error) {
      // A failed test comes back as a 400 whose detail is written for the user ("invalid API key or
      // credentials for this integration") — exactly the question the icon alone cannot answer, since
      // a wrong key and an outage look identical. `getUserFacingErrorMessage` surfaces that detail
      // verbatim. On the statuses whose detail is internal boilerplate (5xx/404, 401) it substitutes
      // this copy instead, appending the server's `Ref: …` correlation id when the response carried
      // one — so the reference stays available to support without standing in as the whole message.
      snackbars.create({
        variant: 'critical',
        id: 'verify.data-out.error',
        message: getUserFacingErrorMessage(error, `We couldn't test the ${integration.name} connection. Try again.`),
      });
    }
  };

  return (
    <InlineStack alignBlock="center" gap="150" mt="200">
      <Tooltip isDisabled={!isDisabled} content="Fill out the form to test the connection." isCustom={true}>
        {(context) => (
          <Button
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            variant="secondary"
            isDisabled={isDisabled}
            onClick={() => {
              void onTest();
            }}
          >
            Test connection
          </Button>
        )}
      </Tooltip>
      <TestResult isPending={verify.isPending} isSuccess={verify.isSuccess} isError={verify.isError} />
    </InlineStack>
  );
}

type TestResultProps = {
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
};

function TestResult({ isPending, isSuccess, isError }: TestResultProps) {
  if (isPending) {
    return <Spinner data-test-verify-result="pending" />;
  }
  if (isSuccess) {
    return <Icon name="check-circle" color="success" data-test-verify-result="success" />;
  }
  if (isError) {
    return <Icon name="critical" color="critical" data-test-verify-result="error" />;
  }
  return null;
}
